import React, { useMemo } from 'react';
import { Anchor, Flag, MapPin, Ship, Truck, RotateCcw, Package, Clock } from 'lucide-react';
import { useDirection } from '@/contexts/DirectionContext';
import { fmtDate, etaLabel } from '@/features/dashboard/data/claudeMockData';
import type { ContainerModalData } from '../interfaces/container.interface';
import { buildVoyage, type LegState, type VoyageLeg, type VoyageStep, type VoyageStop } from '../utils/containerVoyage';

type Lang = 'ro' | 'en';

interface ContainerJourneyTabProps {
  data: ContainerModalData;
  lang: Lang;
}

const ROLE_LABEL: Record<VoyageLeg['role'], { ro: string; en: string }> = {
  vessel: { ro: 'Navă', en: 'Vessel' },
  mother: { ro: 'Navă mamă', en: 'Mother vessel' },
  feeder: { ro: 'Feeder', en: 'Feeder' },
};

const STATE_LABEL: Record<LegState, { ro: string; en: string }> = {
  done: { ro: 'Încheiat', en: 'Completed' },
  active: { ro: 'Pe mare acum', en: 'At sea now' },
  next: { ro: 'Urmează', en: 'Up next' },
  planned: { ro: 'Planificat', en: 'Planned' },
};

const STOP_KIND_LABEL: Record<VoyageStop['kind'], { ro: string; en: string }> = {
  origin: { ro: 'Port de încărcare', en: 'Port of loading' },
  transshipment: { ro: 'Transbordare', en: 'Transshipment' },
  destination: { ro: 'Port de destinație', en: 'Port of discharge' },
};

const STEP_LABEL: Record<string, { ro: string; en: string; icon: React.ElementType }> = {
  ES: { ro: 'Container gol trimis la expeditor', en: 'Empty container sent to shipper', icon: Package },
  FL: { ro: 'Container plin intrat în terminal', en: 'Full container gated in', icon: Package },
  FC: { ro: 'Container plin livrat la importator', en: 'Full container delivered to importer', icon: Truck },
  IE: { ro: 'Container gol redirecționat către un exportator', en: 'Empty container redirected to an exporter', icon: RotateCcw },
  RE: { ro: 'Container returnat gol în terminal', en: 'Empty container returned to terminal', icon: RotateCcw },
};

export const ContainerJourneyTab: React.FC<ContainerJourneyTabProps> = ({ data, lang }) => {
  const { direction } = useDirection();
  const isRo = lang === 'ro';
  const voyage = useMemo(() => buildVoyage(data, direction), [data, direction]);
  const t = (x: { ro: string; en: string }) => (isRo ? x.ro : x.en);

  const dest = voyage.stops[voyage.stops.length - 1];
  // Fără ETA la destinație (export cu ultimul tronson neanunțat): arătăm următoarea escală cu ETA.
  const nextEta = !dest.reached && !dest.arrival ? voyage.stops.find((s) => !s.reached && s.arrival) : undefined;
  const target = nextEta ?? dest;
  const hubs = voyage.stops.filter((s) => s.kind === 'transshipment').map((s) => s.name || s.code);

  return (
    <div>
      <div className="vy-summary">
        <div className="vy-stat">
          <span className="k">{isRo ? 'Timp de tranzit' : 'Transit time'}</span>
          <span className="v">
            {voyage.transitDays != null ? `${voyage.transitDays} ${isRo ? 'zile' : 'days'}` : '—'}
          </span>
          <span className="s">{dest.reached ? (isRo ? 'până la descărcare' : 'until discharge') : isRo ? 'de la plecare până azi' : 'since departure'}</span>
        </div>
        <div className="vy-stat">
          <span className="k">{isRo ? 'Transbordări' : 'Transshipments'}</span>
          <span className="v">{voyage.transshipments}</span>
          <span className="s">{hubs.length ? hubs.join(', ') : isRo ? 'rută directă' : 'direct route'}</span>
        </div>
        <div className="vy-stat">
          <span className="k">
            {target.reached
              ? isRo ? 'Sosit în' : 'Arrived at'
              : nextEta
                ? isRo ? 'Următoarea escală' : 'Next port call'
                : isRo ? 'Sosire estimată' : 'Estimated arrival'}
          </span>
          <span className="v">{target.arrival ? fmtDate(target.arrival, lang) : '—'}</span>
          <span className="s">
            {target.name || target.code || '—'}
            {target.arrival && !target.reached ? ` · ${etaLabel(target.arrival, lang)}` : ''}
          </span>
        </div>
      </div>

      <div className="vy">
        {voyage.before.length > 0 && renderSteps(voyage.before)}

        {voyage.stops.map((stop, i) => (
          <React.Fragment key={`${stop.code}-${i}`}>
            {renderStop(stop)}
            {voyage.legs[i] && renderLeg(voyage.legs[i])}
          </React.Fragment>
        ))}

        {voyage.after.length > 0 && renderSteps(voyage.after)}
      </div>
    </div>
  );

  function renderStop(stop: VoyageStop) {
    const Icon = stop.kind === 'destination' ? Flag : stop.kind === 'origin' ? MapPin : Anchor;
    const cls = 'vy-stop' + (stop.reached ? ' reached' : '') + (stop.current ? ' current' : '');
    return (
      <div className={cls}>
        <div className="vy-node">
          <Icon size={16} strokeWidth={2.2} />
        </div>
        <div className="vy-stop-main">
          <div className="vy-kind">
            {t(STOP_KIND_LABEL[stop.kind])}
            {stop.current && <span className="vy-here">{isRo ? 'Aici acum' : 'Here now'}</span>}
          </div>
          <div className="vy-port">
            {stop.name || stop.code || (isRo ? 'Port necunoscut' : 'Unknown port')}
            {stop.name && stop.code && <span className="mono">{stop.code}</span>}
          </div>
        </div>
        <div className="vy-dates">
          {stop.arrival && (
            <div>
              <span className="muted">{stop.arrivalEstimated ? 'ETA' : isRo ? 'Sosire' : 'Arrived'}</span>{' '}
              {fmtDate(stop.arrival, lang)}
            </div>
          )}
          {stop.departure && (
            <div>
              <span className="muted">{stop.departureEstimated ? 'ETD' : isRo ? 'Plecare' : 'Departed'}</span>{' '}
              {fmtDate(stop.departure, lang)}
            </div>
          )}
          {!stop.arrival && !stop.departure && <div className="muted">—</div>}
        </div>
      </div>
    );
  }

  function renderLeg(leg: VoyageLeg) {
    return (
      <div className={'vy-leg ' + leg.state}>
        <div className="vy-rail">
          {leg.state === 'active' && (
            <span className="vy-ship">
              <Ship size={13} strokeWidth={2.2} />
            </span>
          )}
        </div>
        <div className="vy-leg-card">
          <div className="vy-leg-icon">
            <Ship size={15} strokeWidth={2} />
          </div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div className="vy-leg-role">{t(ROLE_LABEL[leg.role])}</div>
            <div className="vy-leg-vessel">
              {leg.vesselName || (isRo ? 'Navă neanunțată' : 'Vessel not announced')}
              {leg.vesselCode && <span className="mono">{leg.vesselCode}</span>}
            </div>
          </div>
          <div className="vy-leg-side">
            <span className={'vy-pill ' + leg.state}>{t(STATE_LABEL[leg.state])}</span>
            {leg.days != null && (
              <span className="vy-days">
                <Clock size={11} strokeWidth={2} />
                {leg.days} {isRo ? (leg.days === 1 ? 'zi' : 'zile') : leg.days === 1 ? 'day' : 'days'}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  }

  function renderSteps(steps: VoyageStep[]) {
    return (
      <div className="vy-steps">
        {steps.map((s) => {
          const info = STEP_LABEL[s.movement];
          const Icon = info?.icon ?? Package;
          return (
            <div key={s.key} className="vy-step">
              <span className="vy-step-icon">
                <Icon size={13} strokeWidth={2} />
              </span>
              <span className="vy-step-title">{info ? t(info) : s.movement}</span>
              {s.port && <span className="muted mono fs-11">{s.port}</span>}
              <span className="vy-step-date">{fmtDate(s.date, lang)}</span>
            </div>
          );
        })}
      </div>
    );
  }
};
